import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { getContainer } from '@framework/di/getContainer';
import { useRouter } from 'next/router';
import {
  IMarketArticle,
  IMarketArticlesBasePayload,
  IMarketArticlesPayload,
  IMarketArticlesData,
} from '@/creditflowresearch/domain/vo/ArticlesAndStats/Articles/IMarketArticle';
import CreditFlowResearchRepository from '@/creditflowresearch/infrastructure/repository/CreditFlowResearchRepository';
import useFetchCollection from '@/creditflowresearch/infrastructure/state/useFetchCollection';
import { IQueryParams } from '@/creditflowresearch/domain/vo/IQueryParams';
import { getTabParams, getISODateFromUnix } from '@/creditflowresearch/application/ArticlesAndStats/Articles/helpers';
import { initialArticlesCollectionFiltersState } from '@/creditflowresearch/application/ArticlesAndStats/Articles/constants';
import useAutoFetch from '@/creditflowresearch/application/hooks/useAutoFetch';
import { useArticlesFilterContext } from './Filter/ArticlesFilterContext';

const useArticles = ({
  collection,
  payload,
  totalCount,
  hasServerError,
}: IMarketArticlesData) => {
  const container = getContainer();
  const { query: { articleType, productType } }:{ query: IQueryParams } = useRouter();
  const requestService = container.get<CreditFlowResearchRepository>(CreditFlowResearchRepository);
  const [isFirstFetching, setIsFirstFetching] = useState<boolean>(!hasServerError);
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const { state: { filtersPayload } } = useArticlesFilterContext();

  // Next 2 property stored in refs to avoid unnecessary updates
  // this ref contains information if current collection was already extended
  // we need it to avoid unnecessary requests if user click expand-collapse several times.
  const isExpandedCollection = useRef(false);

  // this ref contains information if current state is expanded or not
  // we cant update articlesPayload every time whe user click expand-collapse button
  // because this will cause unnecessary requests
  const currentIsExpanded = useRef(false);
  const [articlesPayload, setArticlesPayload] = useState<IMarketArticlesBasePayload>(payload);

  const fetchCallback = useCallback(async (payload: IMarketArticlesPayload) => (
    // we need to update isExtended here to pass current value to request
    // even without dependency in this hook, we still can get real current value
    // because we store it in ref.

      requestService.getArticlesList({
        ...{
          ...payload,
          filter: {
            ...payload.filter,
            dateFrom: getISODateFromUnix(payload.filter.dateFrom),
            dateTo: getISODateFromUnix(payload.filter.dateTo),
          },
        },
        isExtended: currentIsExpanded.current,
      })
  ), []);

  const articlesData = useFetchCollection<IMarketArticle, IMarketArticlesPayload>(
    fetchCallback,
    {
      enabled: !isFirstFetching,
      payload: articlesPayload as IMarketArticlesPayload,
      initialValues: {
        collection,
        totalCount,
        ...initialArticlesCollectionFiltersState,
        debouncedSearchString: initialArticlesCollectionFiltersState.search,
      },
    },
  );

  useAutoFetch(
    () => setIsFirstFetching(false),
    [articlesPayload, articlesData.page, articlesData.debouncedSearchString],
  );

  useEffect(() => articlesData.reset, []);

  useEffect(() => {
    if (isEqual(articlesPayload.filter, filtersPayload)) {
      return;
    }

    setIsFirstFetching(false);
    setArticlesPayload((prev) => ({
      ...prev,
      filter: filtersPayload,
    }));
  }, [filtersPayload]);

  useEffect(() => {
    // after fetching collection we need to update isExpandedCollection ref
    // to identify if current collection was already extended
    isExpandedCollection.current = isExpanded;
  }, [articlesData.collection]);

  useEffect(() => {
    // on change isExpanded we need to update currentIsExpanded to store current value
    currentIsExpanded.current = isExpanded;
  }, [isExpanded]);

  useAutoFetch(() => {
    // but if collection already expanded we don't need to call fetch method
    if (isExpandedCollection.current) {
      return;
    }
    articlesData.getCollection();
  }, [isExpanded]);

  useEffect(() => {
    const tabParams = getTabParams(articleType, productType);

    if (
      isEqual(articlesPayload.tabParams?.includedThemeType, tabParams?.includedThemeType)
      && isEqual(articlesPayload.tabParams?.includedThemeSubtype, tabParams?.includedThemeSubtype)
    ) {
      return;
    }
    articlesData.paginate(1);
    setArticlesPayload((prev) => ({ ...prev, tabParams, isExtended: isExpanded }));
  }, [articleType, productType]);

  return { articlesData, isExpanded, setIsExpanded };
};

export default useArticles;
